/**
 * C04. Badge + 解鎖儀式感 — 學習端 only.
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md §C04 (Khan Academy badge system).
 * Pillar: Retention + 家長感知學習成果 → 付費意願.
 *
 * Responsibility split:
 *   - `badges-data.json`     — catalogue (id / name / icon / criteria / category).
 *                              Append-only after ship.
 *   - `badges.ts` (this file)— pure evaluator + state mutator. NO DOM imports.
 *                              `evaluateBadges(p)` returns newly-earned ids;
 *                              `unlockBadge(p, id)` mutates `p.badges` and
 *                              dispatches `ef:badge-unlocked`.
 *                              `checkAndUnlockBadges(p)` is the convenience
 *                              wrapper home + lesson runners should call.
 *   - `badges-ui.ts`         — home banner + parent dashboard panel render.
 *
 * 🔥 學習與遊戲分離 (CLAUDE.md 紅線):
 *   This module reads PlayerProgress fields that already exist (bossesDefeated,
 *   unlockedSkills, streak.days, studentKPs). It does NOT import anything from
 *   `src/game/`. The mascot integration is opt-in via the window event
 *   `ef:badge-unlocked` (mascot listens → triggers streak-celebrate animation).
 */

import type { PlayerProgress } from '../../shared/types';
import { saveProgress } from '../../shared/progress';
import { masteryPercent } from '../../shared/student-kp';
import catalogueData from './badges-data.json';

/** Categories — keep in sync with badges-data.json. */
export type BadgeCategory = 'learn' | 'battle' | 'streak' | 'collect' | 'special';

/**
 * Criteria shape — driven by `criteria.type` discriminator. New criteria types
 * are added here + `meetsCriteria()` switch below.
 */
export type BadgeCriteria =
  | { type: 'kp-correct-count'; n: number }
  | { type: 'kp-mastered-count'; n: number }
  | { type: 'chapter-complete-count'; n: number }
  | { type: 'boss-defeat-count'; n: number }
  | { type: 'streak-days'; n: number }
  | { type: 'skill-unlock-count'; n: number }
  | { type: 'perfect-exam-count'; n: number };

export interface BadgeDef {
  id: string;
  name: string;
  icon: string;            // emoji
  category: BadgeCategory;
  criteria: BadgeCriteria;
  description: string;     // shown in kid-facing card
  parentBlurb: string;     // 「媽媽看得懂」 — shown in parent dashboard
}

interface CatalogueJson {
  badges: BadgeDef[];
}

const RAW = catalogueData as unknown as CatalogueJson;
/** Frozen catalogue — never mutate at runtime. */
export const BADGE_CATALOGUE: readonly BadgeDef[] = Object.freeze(RAW.badges);

/** Mastery threshold — a KP is "mastered" once `masteryPercent` >= this AND
 * the kid has answered at least 3 questions for it (so a single luck-correct
 * doesn't count as mastery). Mirrors the parent-dashboard "強單元" filter. */
export const MASTERY_PERCENT_THRESHOLD = 80;
export const MASTERY_MIN_ATTEMPTS = 3;

/** Window event dispatched when a badge is freshly unlocked. */
export interface BadgeUnlockedEventDetail {
  /** The unlocked badge id (lookup full def in BADGE_CATALOGUE). */
  badgeId: string;
  /** Full def for convenience (mascot UI can show name+icon without lookup). */
  badge: BadgeDef;
  /** Unix ms timestamp captured on unlock. */
  at: number;
}

// ── State helpers ───────────────────────────────────────────────────────────

/** Lazy-init the badges sub-object. Mutates p; returns the non-optional shape. */
function getBadges(p: PlayerProgress): NonNullable<PlayerProgress['badges']> {
  if (!p.badges) {
    p.badges = { earned: [] };
  }
  return p.badges;
}

/** True iff the badge id has already been earned. */
export function hasBadge(p: PlayerProgress, badgeId: string): boolean {
  return (p.badges?.earned ?? []).includes(badgeId);
}

/** All earned badge ids (defensive copy). */
export function earnedBadgeIds(p: PlayerProgress): string[] {
  return [...(p.badges?.earned ?? [])];
}

/** All earned badge defs (resolves ids → catalogue defs, skips orphans). */
export function earnedBadgeDefs(p: PlayerProgress): BadgeDef[] {
  const earned = p.badges?.earned ?? [];
  return BADGE_CATALOGUE.filter((b) => earned.includes(b.id));
}

/** Lookup a single badge def by id (null if not in catalogue). */
export function findBadgeDef(id: string): BadgeDef | null {
  return BADGE_CATALOGUE.find((b) => b.id === id) ?? null;
}

// ── Criteria evaluation ─────────────────────────────────────────────────────

/**
 * Count how many KPs the kid has "mastered" (masteryPercent ≥ threshold AND
 * at least MASTERY_MIN_ATTEMPTS attempts logged).
 */
export function masteredKpCount(p: PlayerProgress): number {
  const kps = p.studentKPs ?? [];
  let n = 0;
  for (const kp of kps) {
    if (kp.totalAttempts >= MASTERY_MIN_ATTEMPTS && masteryPercent(kp) >= MASTERY_PERCENT_THRESHOLD) {
      n++;
    }
  }
  return n;
}

/**
 * Count of distinct chapters completed. We treat a chapter as "complete" once
 * its campaignState has visited it — historical chapters appear in `visitedNodes`
 * via chapter-prefixed node ids. Plus the *currently-completed* chapter counter
 * `campaignState.chapter - 1` (chapters before the current one are by definition
 * complete in Slay-Spire flow). Conservative max: 5 (campaign chapter cap).
 *
 * Returns 0 for fresh players or absent campaignState.
 */
export function chaptersCompletedCount(p: PlayerProgress): number {
  const cs = p.campaignState;
  if (!cs) return 0;
  // The "current chapter" is the one in progress, so chapters STRICTLY before
  // it are complete. Chapter numbers are 1-indexed; chapter=1 means none done.
  return Math.max(0, Math.min(5, cs.chapter - 1));
}

/**
 * Tracker for "perfect exam" criterion. Lives on PlayerProgress.badges as a
 * separate counter so the rare "100% on a quiz" event is sticky even after
 * the kid plays another non-perfect session.
 *
 * We piggyback on a side-channel: callers (formal-exam / chapter-quiz UI)
 * invoke `recordPerfectExam(p)` when score === total. That increments a
 * counter we read here. Counter persisted under `__perfectExamCount` to
 * avoid bloating the public type for one number. Stored as a custom key on
 * the badges object; defensive cast keeps TS happy without polluting types.ts.
 */
function perfectExamCount(p: PlayerProgress): number {
  const b = p.badges as { __perfectExamCount?: number } | undefined;
  return typeof b?.__perfectExamCount === 'number' ? b.__perfectExamCount : 0;
}

/**
 * Increment the perfect-exam counter (sticky — never decrements). Mutates p.
 * Caller is responsible for calling `checkAndUnlockBadges(p)` afterwards.
 */
export function recordPerfectExam(p: PlayerProgress): void {
  const badges = getBadges(p) as { earned: string[]; __perfectExamCount?: number } & PlayerProgress['badges'];
  badges.__perfectExamCount = (badges.__perfectExamCount ?? 0) + 1;
}

/** Total correct answers across all KPs. */
function totalCorrectCount(p: PlayerProgress): number {
  return (p.studentKPs ?? []).reduce((sum, kp) => sum + (kp.correctCount ?? 0), 0);
}

/** Number of unlocked skills (excludes cosmetic flags — those start with `cosmetic:`). */
function realSkillUnlockCount(p: PlayerProgress): number {
  return (p.unlockedSkills ?? []).filter((id) => !id.startsWith('cosmetic:')).length;
}

/**
 * True iff the kid's current state meets `criteria`. Pure read-only — no
 * mutations. Exposed for tests.
 */
export function meetsCriteria(p: PlayerProgress, criteria: BadgeCriteria): boolean {
  switch (criteria.type) {
    case 'kp-correct-count':
      return totalCorrectCount(p) >= criteria.n;
    case 'kp-mastered-count':
      return masteredKpCount(p) >= criteria.n;
    case 'chapter-complete-count':
      return chaptersCompletedCount(p) >= criteria.n;
    case 'boss-defeat-count':
      return (p.bossesDefeated?.length ?? 0) >= criteria.n;
    case 'streak-days':
      return (p.streak?.days ?? 0) >= criteria.n;
    case 'skill-unlock-count':
      return realSkillUnlockCount(p) >= criteria.n;
    case 'perfect-exam-count':
      return perfectExamCount(p) >= criteria.n;
    default: {
      // Exhaustive guard — TS will warn here if a new variant is added without a case.
      const _exhaustive: never = criteria;
      void _exhaustive;
      return false;
    }
  }
}

/**
 * Return the list of badge ids the kid newly QUALIFIES for (criteria met,
 * NOT yet in `earned`). Pure — does not mutate `p` and does not dispatch
 * events. Caller decides whether to actually unlock.
 */
export function evaluateBadges(p: PlayerProgress): string[] {
  const earned = new Set(p.badges?.earned ?? []);
  const newly: string[] = [];
  for (const def of BADGE_CATALOGUE) {
    if (earned.has(def.id)) continue;
    if (meetsCriteria(p, def.criteria)) newly.push(def.id);
  }
  return newly;
}

// ── Unlock + event dispatch ─────────────────────────────────────────────────

/**
 * Unlock a single badge. Idempotent — returns false if already earned (no
 * event, no mutation, no save). On success:
 *   1. Append id to `earned`
 *   2. Set `lastUnlocked = { id, at: nowMs }` (drives the home reveal banner
 *      and mascot celebration on next render)
 *   3. Persist via saveProgress
 *   4. Dispatch `ef:badge-unlocked` on window (best-effort; swallow if no DOM)
 *
 * Returns the badge def on success, null otherwise.
 */
export function unlockBadge(
  p: PlayerProgress,
  badgeId: string,
  nowMs: number = Date.now(),
): BadgeDef | null {
  const def = findBadgeDef(badgeId);
  if (!def) return null;
  const badges = getBadges(p);
  if (badges.earned.includes(badgeId)) return null;
  badges.earned.push(badgeId);
  badges.lastUnlocked = { id: badgeId, at: nowMs };
  try {
    saveProgress(p);
  } catch {
    // Test env may not have localStorage hooked; the mutation already
    // happened in-memory so callers can still observe the change.
  }
  if (typeof window !== 'undefined') {
    try {
      const detail: BadgeUnlockedEventDetail = { badgeId, badge: def, at: nowMs };
      window.dispatchEvent(new CustomEvent('ef:badge-unlocked', { detail }));
    } catch {
      // jsdom without CustomEvent shim — ignore; state already mutated.
    }
  }
  return def;
}

/**
 * Convenience wrapper: evaluate criteria + unlock everything newly-qualified
 * in one call. Returns the list of badge defs unlocked on this tick (empty
 * when nothing new qualifies). Each unlock fires its own event so the mascot
 * + home banner can react in sequence (the home banner uses `lastUnlocked`
 * which always reflects the most recent one).
 *
 * Designed to be called from:
 *   - home render (catches battle-side / streak-side qualifications)
 *   - lesson end (catches kp-correct-count + kp-mastered-count after a session)
 *   - chapter completion (chapter-complete-count)
 *   - exam result (perfect-exam-count via recordPerfectExam first)
 *   - boss defeat hook (battle scene → main.ts already saves; we re-check)
 */
export function checkAndUnlockBadges(
  p: PlayerProgress,
  nowMs: number = Date.now(),
): BadgeDef[] {
  const newlyQualified = evaluateBadges(p);
  const unlocked: BadgeDef[] = [];
  for (const id of newlyQualified) {
    const def = unlockBadge(p, id, nowMs);
    if (def) unlocked.push(def);
  }
  return unlocked;
}

/**
 * Read `lastUnlocked` once and CLEAR it. Home banner uses this so the
 * "🎉 解鎖新徽章!" reveal fires exactly once per unlock event. Persists
 * via saveProgress.
 */
export function consumeLastUnlocked(p: PlayerProgress): BadgeDef | null {
  const lu = p.badges?.lastUnlocked;
  if (!lu) return null;
  const def = findBadgeDef(lu.id);
  if (p.badges) {
    delete p.badges.lastUnlocked;
    try {
      saveProgress(p);
    } catch {
      // ignore — see unlockBadge
    }
  }
  return def;
}

// ── Snapshot for UI ─────────────────────────────────────────────────────────

export interface BadgeProgress {
  def: BadgeDef;
  earned: boolean;
  /** Unix ms timestamp when earned (only set for earned badges). */
  earnedAt?: number;
}

/**
 * Build a per-badge snapshot for the parent dashboard / home panel. Sorted:
 * earned first (most recent first via `lastUnlocked.at`), then locked.
 */
export function buildBadgeSnapshot(p: PlayerProgress): BadgeProgress[] {
  const earnedSet = new Set(p.badges?.earned ?? []);
  const lastUnlocked = p.badges?.lastUnlocked;
  return BADGE_CATALOGUE.map<BadgeProgress>((def) => ({
    def,
    earned: earnedSet.has(def.id),
    earnedAt: lastUnlocked?.id === def.id ? lastUnlocked.at : undefined,
  }));
}

/** Group snapshot by category — handy for parent dashboard "by category" view. */
export function snapshotByCategory(p: PlayerProgress): Record<BadgeCategory, BadgeProgress[]> {
  const groups: Record<BadgeCategory, BadgeProgress[]> = {
    learn: [],
    battle: [],
    streak: [],
    collect: [],
    special: [],
  };
  for (const entry of buildBadgeSnapshot(p)) {
    groups[entry.def.category].push(entry);
  }
  return groups;
}

/** Category display labels (Chinese, kid-friendly). */
export const CATEGORY_LABELS: Record<BadgeCategory, string> = {
  learn: '📚 學習',
  battle: '⚔️ 戰鬥',
  streak: '🔥 連登',
  collect: '🎯 收集',
  special: '🌟 特殊',
};
